/**
 * usePushHealth — Plan 06-04 Task 2.
 *
 * TanStack Query hook for GET /api/admin/push/health. Renders inside
 * <AdminDashboardPanel>. Family-scale data — fetched on demand only when an
 * admin opens the panel, so `staleTime: 0` (always treat as stale; refetch
 * when the panel mounts). No cache pressure at single-household size.
 *
 * The server route is gated by requireAdminScope — non-admin / expired-scope
 * sessions surface as 403 here. Feilen kastes som `ApiError`, slik at
 * `<PushHealthTable>` kan skille en manglende admin-scope (→ «Lås opp») fra
 * andre feil (→ <ErrorState>).
 *
 * Why credentials:'same-origin' (not 'include'): the panel is mounted at
 * /settings/admin, which is always same-origin.
 * 'same-origin' is the safer default — never sends cookies across origins
 * even if the page is mistakenly served from a different host during
 * development.
 */
import { useQuery } from '@tanstack/react-query';
import type { PushHealthRow } from '@stell/shared';
import { toApiError } from './useMembers';

export interface PushHealthResponse {
  rows: PushHealthRow[];
}

export function usePushHealth() {
  return useQuery<PushHealthResponse>({
    queryKey: ['admin', 'push', 'health'],
    staleTime: 0,
    queryFn: async () => {
      const res = await globalThis.fetch('/api/admin/push/health', {
        credentials: 'same-origin',
      });
      if (!res.ok) throw await toApiError(res);
      return (await res.json()) as PushHealthResponse;
    },
  });
}
